import { Loader, Stack } from "@mantine/core";
import { type TieredPriceProductId, TIERED_PRODUCT_IDS } from "@project/common";
import { useMemo } from "react";
import { FormattedMessage } from "react-intl";
import { EChartTimeseries } from "../../../../components/EChartTimeseries.js";
import {
  type StackedBarLayer,
  useMemoizedStackedBarSeries,
  normalizeStackedLayers,
} from "../../../../hooks/chart.js";
import { ErrorAlert } from "../../components/ErrorAlert.js";
import { useProductNames } from "../../components/hooks/productLabels.js";
import { useStackedChartColors } from "../hooks/chartColors.js";

interface StackedChartProps {
  metrics: TieredPriceProductId[];
  allDailyCosts: Record<TieredPriceProductId, [number, number][]>;
  isLoadingDailyCosts: boolean;
  isErrorDailyCosts: boolean;
}

export function StackedChart({
  metrics,
  allDailyCosts,
  isLoadingDailyCosts,
  isErrorDailyCosts,
}: StackedChartProps) {
  const getName = useProductNames();
  const chartColors = useStackedChartColors();

  const layers: StackedBarLayer[] = useMemo(() => TIERED_PRODUCT_IDS.map(productId => ({
    id: productId,
    name: getName(productId),
    color: chartColors[productId],
    data: allDailyCosts[productId],
  })), [allDailyCosts, getName, chartColors]);

  const series = useMemoizedStackedBarSeries(
    normalizeStackedLayers(
      layers.filter(layer => metrics.includes(layer.id as TieredPriceProductId)),
    ),
  );

  if (isLoadingDailyCosts) {
    return <Loader />;
  }
  else if (isErrorDailyCosts) {
    return (
      <ErrorAlert>
        <FormattedMessage
          defaultMessage="Error creating stacked chart of daily usage costs"
          id="stackedChart.error"
        />
      </ErrorAlert>
    );
  }
  else {
    return (
      <Stack h={400}>
        <EChartTimeseries series={series} showLegend={true} isUSD={true} />
      </Stack>
    );
  }
}
